import React, { useMemo, useState } from 'react'
import ForgotPassword from '../../components/auth/ForgotPassword'
import ForgotPasswordModal from '../../components/auth/ForgotPasswordModal'

import {
    forgotPasswordFormValid,
    restPasswordFormValid,
} from '../../validation'
import {
    convertErrorYup,
    ensureArray,
    errorHandle,
    showMessageSuccess,
} from '../../utils/helper'
import { useDispatch } from 'react-redux'
import { forgotPassword, resetPassword } from '../../actions/accountActions'
import useDidMountEffect from '../../hooks/useDidMountEffect'
import { useTranslation } from 'react-i18next'

const defaultForm = {}

const ForgotPasswordContainer = ({ isOpen, setIsOpen, isModal }) => {
    const dispatch = useDispatch()
    const [form, setForm] = useState({ ...defaultForm })
    const [errors, setErrors] = useState({})
    const [currentStep, setCurrentStep] = useState(0)

    const { t } = useTranslation()

    const handleInputChange = e => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const clearErrorField = field =>
        setErrors(prev => ({ ...prev, [field]: '' }))

    const handleRequestForgot = async e => {
        e && e.preventDefault()

        try {
            await forgotPasswordFormValid.validate(form, { abortEarly: false })

            dispatch(
                forgotPassword({
                    params: form,
                    onDone: ({ responseData, success }) => {
                        if (success) {
                            // show message success
                            setForm(prev => ({
                                ...prev,
                                idHash: responseData.data?.idHash,
                            }))

                            setCurrentStep(prev => prev + 1)
                        } else {
                            if (responseData?.data) {
                                setErrors(
                                    ensureArray(responseData?.data).reduce(
                                        (rs, current) => ({
                                            ...rs,
                                            [current.field]: current.message,
                                        }),
                                        {}
                                    )
                                )
                            } else {
                                errorHandle({
                                    message:
                                        'apiMessage.requestForgetPasswordFailed',
                                })
                            }
                        }
                    },
                })
            )
        } catch (error) {
            if (error?.name === 'ValidationError') {
                setErrors(convertErrorYup(error))
            }
        }
    }

    const handleSubmitChangePassword = async e => {
        e && e.preventDefault()

        try {
            const payload = {
                otp: form.otp,
                newPassword: form.newPassword,
                idHash: form.idHash,
            }
            await restPasswordFormValid.validate(
                {
                    ...payload,
                    confirmNewPassword: form.confirmNewPassword,
                },
                { abortEarly: false }
            )

            dispatch(
                resetPassword({
                    params: payload,
                    onDone: ({ responseData, success }) => {
                        if (success) {
                            showMessageSuccess(
                                t('forgotPassword.mesChangeSuccess')
                            )
                            setCurrentStep(0)
                            setForm({})
                            isModal && setIsOpen(false)
                        } else {
                            if (responseData?.data) {
                                setErrors(
                                    ensureArray(responseData?.data).reduce(
                                        (rs, current) => ({
                                            ...rs,
                                            [current.field]: current.message,
                                        }),
                                        {}
                                    )
                                )
                            } else {
                                errorHandle({
                                    message: 'apiMessage.changePasswordFailed',
                                })
                            }
                        }
                    },
                })
            )
        } catch (error) {
            if (error?.name === 'ValidationError') {
                setErrors(convertErrorYup(error))
            }
        }
    }

    const submitStep = [handleRequestForgot, handleSubmitChangePassword]

    const DynamicComponent = useMemo(() => {
        return isModal ? ForgotPasswordModal : ForgotPassword
    }, [isModal])

    useDidMountEffect(() => {
        if (isModal && !isOpen) {
            setForm({ ...defaultForm })
            setErrors({})
            setCurrentStep(0)
        }
    }, [isOpen])

    return (
        <DynamicComponent
            handleSubmit={submitStep[currentStep]}
            currentStep={currentStep}
            form={form}
            handleInputChange={handleInputChange}
            clearErrorField={clearErrorField}
            errors={errors}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
        />
    )
}

export default ForgotPasswordContainer
